import { IAccordionListNode } from '@opengov/component-library/capital/AccordionList/AccordionList';
import { noneNode } from './AccordianSelectDropdown/AccordianSelectDropdown';
import { ETocSeparator, ITocStyles, TOC_NODE_ID, ITocFontType } from '@opengov/stories-shared/constants';
import { IStoryMargins } from '@opengov/stories-shared/types';

export const tocNode = {
  id: TOC_NODE_ID,
  displayName: 'Table of Contents',
  disabled: false,
  toggled: true,
  children: []
};

export const getFontFamily = (fontType: ITocFontType) => {
  switch (fontType) {
    case 'Helvetica':
      return 'Helvetica, Arial, sans-serif';
    case 'Times':
      return 'Times, Arial, sans-serif';
    case 'Courier':
      return 'Courier, Arial, sans-serif';
    case 'DMSans':
      return 'DM Sans, Arial, sans-serif';
    default:
      return 'Helvetica, Arial, sans-serif';
  }
};

const renderTOC = (data: IAccordionListNode[], level = 0, separator: ETocSeparator): string => {
  return `
  <ul class="tocList" style="padding-left: ${level > 0 ? '24px' : '0px'}">
    ${data
      .map((item) => {
        const hasChildren = item.children && item.children.length > 0;
        return hasChildren
          ? `<li class="sectionTitle" style="margin-top: ${level === 0 ? '16px' : '0px'}">
              <p>${item.displayName}</p>
              ${renderTOC(item.children || [], level + 1, separator)}
            </li>`
          : `<li class="storyTitle" style="justify-content: ${separator === ETocSeparator.Dotted ? 'flex-start' : 'space-between'
          }">
              <p class="storyTitleText">${item.displayName}</p>
              ${(separator === ETocSeparator.Dotted && '<div class="separator"></div>') || ''}
              <p class="storyTitleText">x</p>
            </li>`;
      })
      .join('')}
  </ul>
  `;
};

// Convert inches to pixels for CSS (96px per inch)
// Using standard conversion to match exported PDF margins (72 points/inch ≈ 96px/inch at standard DPI)
const inchesToPixels = (inches: number): string => {
  return (inches * 96).toFixed(2);
};

// Generate margin CSS for TOC
// Note: Using same conversion for all margins (no 1.81 multiplier) to match exported PDF
const generateTocMarginStyles = (margins?: IStoryMargins): string => {
  if (!margins) {
    // Default padding if no margins provided (maintains backward compatibility)
    return `
  body {
    padding: 44px 64px;
  }`;
  }

  // Convert margins from inches to pixels (consistent with exported PDF: 1 inch = 72 points ≈ 96px)
  const paddingTop = inchesToPixels(margins.marginTop || 0.25);
  const paddingBottom = inchesToPixels(margins.marginBottom || 0.25);
  const paddingLeft = inchesToPixels(margins.marginLeft || 1);
  const paddingRight = inchesToPixels(margins.marginRight || 1);

  return `
  @page {
    margin-top: ${paddingTop}px !important;
    margin-bottom: ${paddingBottom}px !important;
  }

  body {
    padding: ${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px;
  }`;
};

export const tocHtmlTemplate = (storiesList: IAccordionListNode[], tocStyles: ITocStyles, margins?: IStoryMargins): string => {
  const { fontType, separator } = tocStyles;
  const marginStyles = generateTocMarginStyles(margins);
  return `
<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600&display=swap" rel="stylesheet">
<title>Table of Contents</title>
<style>
  ${marginStyles}

  body, h1, li, ul, p, div {
    font-family: '${getFontFamily(fontType)}';
  }
  h1 {
    font-size: 32px;
    font-weight: 600;
    margin: 0px;
    color: #000000;
  }

  .divider {
    width: 60px;
    height: 4px;
    background-color: #3E5DE6;
  }

  .tocContainer {
    display: flex;
    overflow: auto;
    width: 100%;
    flex-direction: column;
  }

  .tocList {
    display: list-item;
    list-style-type: none;
  }

  .sectionTitle {
    font-size: 16px;
    font-weight: 600;
    color: #494A4C;
    margin: 0px;
    padding: 4px 16px;
  }

  .storyTitle {
    display: flex;
    justify-content: space-between;
    padding: 4px 8px;
    font-size: 16px;
    font-weight: 400;
    color: #494A4C;
    margin: 0px;
    align-items: baseline;
  }

  .titleColor {
    color: #000000;
  }

  p {
    margin: 0px;
  }

  .separator {
    flex: 1;
    border-bottom: 1px dotted #494A4C;
    margin: 0 4px;
  }

  .storyTitleText {
    flex-shrink: 0;
    color: #494A4C;
  }
</style>
</head>
<body>
<div id="toc" class="tocContainer">
  <h1>Table of Contents</h1>
  <div class="divider"></div>
  ${renderTOC(storiesList, 0, separator)}
</div>
</body>
</html>`;
};

export const getStoriesListWithCoverStory = (
  storiesList: IAccordionListNode[],
  coverStoryId: string | null
): IAccordionListNode[] => {
  if (!coverStoryId || coverStoryId === noneNode.id) return [tocNode, ...storiesList];

  const removeStoryNodeById = (
    nodes: IAccordionListNode[]
  ): { nodes: IAccordionListNode[]; removedNode: IAccordionListNode | null } => {
    let removedNode: IAccordionListNode | null = null;

    const filteredNodes = nodes.reduce<IAccordionListNode[]>((acc, node) => {
      if (node.id === coverStoryId) {
        if (!removedNode) removedNode = node;
        return acc;
      }

      if (node.children && Array.isArray(node.children)) {
        const result = removeStoryNodeById(node.children);
        if (!removedNode && result.removedNode) removedNode = result.removedNode;
        if (result.nodes.length > 0) {
          acc.push({ ...node, children: result.nodes });
        }
      } else {
        acc.push(node);
      }

      return acc;
    }, []);

    return { nodes: filteredNodes, removedNode };
  };

  const { nodes, removedNode: coverStoryNode } = removeStoryNodeById(storiesList);

  return coverStoryNode ? [coverStoryNode, tocNode, ...nodes] : [tocNode, ...nodes];
};

const flattenChildrenToSingleSection = (storiesList: IAccordionListNode[]): IAccordionListNode[] => {
  // This function flattens a nested stories list so that all leaf nodes (stories) are at the top level (level 1).
  // It ignores section/folder hierarchy and returns a flat array of all stories.
  const flatten = (nodes: IAccordionListNode[]): IAccordionListNode[] => {
    let result: IAccordionListNode[] = [];
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        result = result.concat(flatten(node.children));
      } else {
        result.push({ ...node, children: [] });
      }
    });
    return result;
  };
  return flatten(storiesList);
};

export const getStoriesListWithChildrenAtSameLevel = (storiesList: IAccordionListNode[]): IAccordionListNode[] => {
  return storiesList.map((node) => {
    const updatedNode = { ...node, children: flattenChildrenToSingleSection(node.children || []) };
    return updatedNode;
  });
};

export const getStoriesIds = (storiesList: IAccordionListNode[], coverStoryId: string | null, toggledStoriesVisibilityMap: { [nodeId: string]: boolean }): string[] => {
  const storiesIds: string[] = [];
  const getStoriesIds = (nodes: IAccordionListNode[], toggledStoriesVisibilityMap: { [nodeId: string]: boolean }): void => {
    nodes.forEach((node) => {
      if (node.children && node.children.length > 0) {
        getStoriesIds(node.children, toggledStoriesVisibilityMap);
      } else {
        if (node.id !== coverStoryId && toggledStoriesVisibilityMap[node.id]) {
          storiesIds.push(node.id);
        }
      }
    });
  };
  getStoriesIds(storiesList, toggledStoriesVisibilityMap);

  if (toggledStoriesVisibilityMap[TOC_NODE_ID]) {
    storiesIds.unshift(TOC_NODE_ID);
  }
  if (coverStoryId && coverStoryId !== noneNode.id) {
    storiesIds.unshift(coverStoryId);
  }
  return storiesIds;
};
/**
 * Parses the stored tocData string into visible nodes and an explicit hidden-ID set.
 *
 * Two serialization formats are supported:
 *   - Legacy (array): `[...visibleNodes]` — any story absent from the list was
 *     either hidden or never existed; we can't distinguish them.
 *   - Current (object): `{ nodes: [...visibleNodes], hiddenIds: [...] }` — only
 *     stories whose IDs appear in `hiddenIds` are explicitly hidden; stories absent
 *     from both lists are newly added and should default to visible.
 */
function parseTocDataString(tocDataString: string): {
  tocNodes: IAccordionListNode[];
  hiddenIds: Set<string> | null; // null = legacy format
} {
  try {
    const parsed = JSON.parse(tocDataString || '[]') as IAccordionListNode[] | { nodes: IAccordionListNode[]; hiddenIds: string[] };
    if (Array.isArray(parsed)) {
      return { tocNodes: parsed, hiddenIds: null };
    }
    return {
      tocNodes: parsed.nodes ?? [],
      hiddenIds: new Set<string>(parsed.hiddenIds ?? [])
    };
  } catch {
    return { tocNodes: [], hiddenIds: new Set<string>() };
  }
}

export const getstoriesVisibilityMap = ({ storiesList, tocDataString, coverStoryId }: { storiesList: IAccordionListNode[], tocDataString: string, coverStoryId: string | null }): { [nodeId: string]: boolean } => {
  const storiesVisibilityMap: { [nodeId: string]: boolean } = {};
  const { tocNodes, hiddenIds } = parseTocDataString(tocDataString);
  const storiesInToc = flattenChildrenToSingleSection(tocNodes);
  const tocIds = new Set(storiesInToc.map(node => node.id));
  const storiesInSelection = flattenChildrenToSingleSection(storiesList);
  storiesInSelection.push(tocNode);

  const hasKnownState = tocNodes.length > 0 || (hiddenIds !== null && hiddenIds.size > 0);
  if (hasKnownState) {
    if (hiddenIds === null) {
      // Legacy format: absence from tocIds means hidden (old behaviour).
      storiesInSelection.forEach((node) => {
        storiesVisibilityMap[node.id] = tocIds.has(node.id);
      });
    } else {
      // Current format: only stories in hiddenIds are hidden.
      // Stories absent from hiddenIds (whether previously visible or newly added) are
      // treated as visible — the icon, getStoriesIds, and visibleStories all use
      // truthy checks, so we must set explicit `true` rather than leaving undefined.
      storiesInSelection.forEach((node) => {
        storiesVisibilityMap[node.id] = !hiddenIds.has(node.id);
      });
    }
  } else {
    // No prior state at all — show everything.
    storiesInSelection.forEach((node) => {
      storiesVisibilityMap[node.id] = true;
    });
  }

  if (coverStoryId && coverStoryId !== noneNode.id) {
    storiesVisibilityMap[coverStoryId] = true;
  }
  return storiesVisibilityMap;
}
export const generateTocHtml = (tocData: IAccordionListNode[], tocStyles: ITocStyles, margins?: IStoryMargins): string => {
  const tocHtml = tocHtmlTemplate(tocData, tocStyles, margins);
  return tocHtml;
};

export const filterNodesByVisibility = (nodes: IAccordionListNode[], toggledStoriesVisibilityMap: { [nodeId: string]: boolean }): IAccordionListNode[] => {
  return nodes.reduce<IAccordionListNode[]>((acc, node) => {
    if (node.children && node.children.length > 0) {
      const filteredChildren = filterNodesByVisibility(node.children, toggledStoriesVisibilityMap);
      if (filteredChildren.length > 0) {
        acc.push({ ...node, children: filteredChildren });
      }
      return acc;
    } else {
      const isVisible = toggledStoriesVisibilityMap[node.id] !== false;
      if (isVisible) {
        acc.push(node);
      }
      return acc;
    }
  }, []);
}
